import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { driver } from "driver.js";
import { take } from "rxjs/operators";
import { GuideSettingApiService } from "../../../services/announcement/guide-setting-api.service";
import { OnboardingTrackerService } from "../../../services/onboarding/onboarding-tracker.service";
import { environment } from "../../../../environments/environment";
import { ONBOARDING_TOURS } from "../config/onboarding-tour-steps.config";
import { TourKey, TourStep } from "../models/tour-step.model";

const STORAGE_PREFIX = "onboarding_tour_seen_";

/**
 * Thin wrapper around driver.js that turns the static step catalog in
 * onboarding-tour-steps.config.ts into a spotlight tour of the current page.
 *
 * - startIfFirstVisit(): shows the tour once per browser (localStorage flag),
 *   waiting `delayMs` for async page data to render before checking the DOM.
 * - restart(): re-opens a tour on demand (e.g. a "Show me again" button),
 *   ignoring the seen flag.
 *
 * Steps whose selector isn't found in the DOM are dropped rather than
 * breaking the tour, so the same definition works across empty/populated
 * states and optional (role-gated) UI.
 */
@Injectable({
  providedIn: "root",
})
export class OnboardingTourService {
  private activeDriver: ReturnType<typeof driver> | null = null;
  private readonly pendingKeys = new Set<TourKey>();

  /**
   * Guides switched off from the back office, or null while the settings are still unknown.
   * The subscription is kept for the life of the service so a toggle in Guide Management
   * lands here — and hides the header button — without a page reload.
   */
  private disabledKeys: Set<string> | null = null;

  constructor(
    private translate: TranslateService,
    private guideSettings: GuideSettingApiService,
    private onboardingTracker: OnboardingTrackerService,
  ) {
    if (this.isEnabled) {
      this.guideSettings
        .disabledKeys()
        .subscribe((keys) => (this.disabledKeys = new Set(keys)));
    }
  }

  /**
   * False when an admin has turned this specific interface's guide off — and while the
   * settings have not been read yet, so a disabled guide is never offered in the window
   * before its setting arrives.
   */
  isGuideEnabled(key: TourKey): boolean {
    return this.isEnabled && this.disabledKeys !== null && !this.disabledKeys.has(key);
  }

  /** Master switch, checked by every entry point below — see environment.featureGuideEnabled. */
  get isEnabled(): boolean {
    return environment.featureGuideEnabled;
  }

  startIfFirstVisit(key: TourKey, delayMs = 600): void {
    if (!this.isEnabled) {
      return;
    }
    // A tour can legitimately be requested twice for the same key — e.g. the
    // header's setup checklist navigates to a page whose own ngOnInit also
    // requests it. De-dupe on the key so the two calls don't race and start
    // (and immediately restart) two driver.js instances back to back.
    if (this.hasSeen(key) || this.pendingKeys.has(key)) {
      return;
    }
    this.pendingKeys.add(key);
    window.setTimeout(() => {
      this.pendingKeys.delete(key);
      // The settings may still be in flight after the delay. Waiting for them is what stops
      // a guide an admin switched off from running once on a slow connection.
      this.guideSettings
        .disabledKeys()
        .pipe(take(1))
        .subscribe(() => {
          if (this.isGuideEnabled(key)) {
            this.tryStart(key, false);
          }
        });
    }, delayMs);
  }

  restart(key: TourKey): void {
    if (!this.isGuideEnabled(key)) {
      return;
    }
    this.tryStart(key, true);
  }

  hasSeen(key: TourKey): boolean {
    try {
      return localStorage.getItem(STORAGE_PREFIX + key) === "1";
    } catch {
      return true;
    }
  }

  private tryStart(key: TourKey, force: boolean): void {
    if (!force && this.hasSeen(key)) {
      return;
    }

    const definition = ONBOARDING_TOURS[key];
    const availableSteps = (definition?.steps ?? []).filter((step) =>
      this.isStepVisible(step.element),
    );
    if (availableSteps.length === 0) {
      return;
    }

    this.activeDriver?.destroy();
    this.startDriver(key, availableSteps);
  }

  /** Separated so tests can assert whether a tour actually ran. */
  private startDriver(key: TourKey, availableSteps: TourStep[]): void {
    this.activeDriver = driver({
      showProgress: availableSteps.length > 1,
      allowClose: true,
      overlayColor: "#094259",
      overlayOpacity: 0.55,
      stagePadding: 6,
      stageRadius: 10,
      animate: true,
      popoverClass: "dashy-tour-popover",
      nextBtnText: this.translate.instant("ONBOARDING_TOUR.COMMON.NEXT"),
      prevBtnText: this.translate.instant("ONBOARDING_TOUR.COMMON.PREVIOUS"),
      doneBtnText: this.translate.instant("ONBOARDING_TOUR.COMMON.DONE"),
      progressText: "{{current}} / {{total}}",
      steps: availableSteps.map((step) => ({
        element: step.element,
        popover: {
          title: this.translate.instant(step.titleKey),
          description: this.translate.instant(step.descriptionKey),
          side: step.side,
          align: step.align,
        },
      })),
      onDestroyed: () => {
        // Where the user stopped is the interesting part: a guide everyone abandons on step 2
        // is a guide (or a screen) that needs fixing, and that only shows up in the journal.
        const stoppedAt = this.activeDriver?.getActiveIndex();
        const reachedEnd = stoppedAt === undefined || stoppedAt >= availableSteps.length - 1;
        this.onboardingTracker.tour(
          key,
          reachedEnd ? "SUCCESS" : "CANCELLED",
          reachedEnd
            ? `Guide ${key} completed`
            : `Guide ${key} dismissed at step ${(stoppedAt ?? 0) + 1}/${availableSteps.length}`,
        );
        this.markSeen(key);
        this.activeDriver = null;
      },
    });

    this.onboardingTracker.tour(key, "STARTED", `Guide ${key} shown`);
    this.activeDriver.drive();
  }

  /**
   * A step is usable only if its element is actually rendered. Being in the DOM is not
   * enough: responsive utilities (e.g. the header setup pill is `hidden lg:block`) and
   * permission-gated toolbars leave elements present but with no box, and spotlighting
   * one of those produces an empty highlight in the corner of the screen.
   */
  private isStepVisible(selector: string): boolean {
    const element = document.querySelector(selector);
    if (!(element instanceof HTMLElement)) {
      return false;
    }
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  private markSeen(key: TourKey): void {
    try {
      localStorage.setItem(STORAGE_PREFIX + key, "1");
    } catch {
      /* private browsing / storage disabled — tour just replays next visit */
    }
  }
}
